import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
} from 'lucide-react';

interface HeaderProps {
  user_name?: string;
  user_email?: string;
  user_avatar?: string;
  notification_count?: number;
  on_logout?: () => void;
  on_profile_click?: () => void;
  on_settings_click?: () => void;
  on_mobile_menu_toggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  user_name = 'John Doe',
  user_email = 'john@example.com',
  user_avatar,
  notification_count = 0,
  on_logout,
  on_profile_click,
  on_settings_click,
  on_mobile_menu_toggle
}) => {
  const [is_profile_open, set_is_profile_open] = useState(false);
  const [is_search_focused, set_is_search_focused] = useState(false);
  const [is_notifications_open, set_is_notifications_open] = useState(false);
  const dropdown_ref = useRef<HTMLDivElement>(null);
  const notifications_ref = useRef<HTMLDivElement>(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handle_click_outside = (event: MouseEvent) => {
      if (dropdown_ref.current && !dropdown_ref.current.contains(event.target as Node)) {
        set_is_profile_open(false);
      }
      if (notifications_ref.current && !notifications_ref.current.contains(event.target as Node)) {
        set_is_notifications_open(false);
      }
    };
    
    document.addEventListener('mousedown', handle_click_outside);
    return () => document.removeEventListener('mousedown', handle_click_outside);
  }, []);
  
  const get_initials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Mock notifications data
  const notifications = [
    { id: 1, title: 'New meter reading', message: 'Meter #M-2024-001 reading submitted', time: '5m ago', unread: true },
    { id: 2, title: 'Payment received', message: 'Invoice #INV-2024-045 paid', time: '1h ago', unread: true },
    { id: 3, title: 'Overdue invoice', message: 'Invoice #INV-2024-032 is overdue', time: '2h ago', unread: false }
  ];
  
  return (
    <header className="bg-white border-b border-light-gray sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Section - Mobile Menu & Search */}
          <div className="flex items-center gap-3 flex-1">
            {/* Mobile Menu Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={on_mobile_menu_toggle}
              className="lg:hidden p-2 rounded-lg text-medium-gray hover:bg-off-white hover:text-charcoal transition-all duration-200"
              aria-label="Toggle mobile menu"
            >
              <Menu size={20} />
            </motion.button>
            
            {/* Search Bar */}
            <div className="hidden md:block flex-1 max-w-md">
              <div className="relative">
                <Search
                  size={18}
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-200 ${
                    is_search_focused ? 'text-forest-green' : 'text-medium-gray'
                  }`}
                />
                <input
                  type="text"
                  placeholder="Search clients, meters, invoices..."
                  className={`w-full pl-10 pr-4 py-2 bg-off-white border border-transparent rounded-lg text-sm transition-all duration-200 focus:outline-none placeholder:text-medium-gray ${
                    is_search_focused
                      ? 'bg-white border-forest-green shadow-sm'
                      : 'hover:bg-white hover:border-light-gray'
                  }`}
                  onFocus={() => set_is_search_focused(true)}
                  onBlur={() => set_is_search_focused(false)}
                />
              </div>
            </div>

            {/* Mobile Search Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="md:hidden p-2 rounded-lg text-medium-gray hover:bg-off-white hover:text-charcoal transition-all duration-200"
              aria-label="Search"
            >
              <Search size={20} />
            </motion.button>
          </div>
          
          {/* Right Section - Notifications & Profile */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative" ref={notifications_ref}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => set_is_notifications_open(!is_notifications_open)}
                className="relative p-2 rounded-lg text-medium-gray hover:bg-off-white hover:text-charcoal transition-all duration-200"
                aria-label="Notifications"
              >
                <Bell size={20} />
                {notification_count > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-1 right-1 w-5 h-5 bg-forest-green text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm"
                  >
                    {notification_count > 9 ? '9+' : notification_count}
                  </motion.span>
                )}
              </motion.button>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {is_notifications_open && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-light-gray overflow-hidden"
                  >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-light-gray">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-charcoal">Notifications</h3>
                        <button className="text-xs text-forest-green hover:text-dark-forest font-medium">
                          Mark all read
                        </button>
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map((notification) => (
                        <button
                          key={notification.id}
                          className={`w-full px-4 py-3 text-left hover:bg-off-white transition-all duration-200 border-b border-light-gray last:border-b-0 ${
                            notification.unread ? 'bg-lime-green bg-opacity-5' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                              notification.unread ? 'bg-forest-green' : 'bg-transparent'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-charcoal mb-0.5">
                                {notification.title}
                              </p>
                              <p className="text-xs text-medium-gray mb-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-medium-gray">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-light-gray bg-off-white">
                      <button className="text-xs text-forest-green hover:text-dark-forest font-medium">
                        View all notifications →
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Profile Dropdown */}
            <div className="relative" ref={dropdown_ref}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => set_is_profile_open(!is_profile_open)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                  is_profile_open ? 'bg-off-white' : 'hover:bg-off-white'
                }`}
              >
                {/* Avatar */}
                <div className="w-8 h-8 rounded-lg bg-forest-green text-white flex items-center justify-center font-semibold text-sm overflow-hidden shadow-sm">
                  {user_avatar ? (
                    <img src={user_avatar} alt={user_name} className="w-full h-full object-cover" />
                  ) : (
                    get_initials(user_name)
                  )}
                </div>
                
                {/* User Info - Hidden on mobile */}
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-medium text-charcoal leading-tight">{user_name}</p>
                  <p className="text-xs text-medium-gray leading-tight">{user_email}</p>
                </div>
                
                <ChevronDown
                  size={16}
                  className={`hidden lg:block text-medium-gray transition-transform duration-200 ${
                    is_profile_open ? 'rotate-180' : ''
                  }`}
                />
              </motion.button>
              
              {/* Dropdown Menu */}
              <AnimatePresence>
                {is_profile_open && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-light-gray overflow-hidden"
                  >
                    {/* User Info in Dropdown (Mobile) */}
                    <div className="lg:hidden px-4 py-3 border-b border-light-gray bg-off-white">
                      <p className="text-sm font-medium text-charcoal">{user_name}</p>
                      <p className="text-xs text-medium-gray">{user_email}</p>
                    </div>
                    
                    <div className="py-1">
                      <button
                        onClick={() => {
                          set_is_profile_open(false);
                          on_profile_click?.();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal hover:bg-off-white transition-all duration-200 group"
                      >
                        <User size={16} className="text-medium-gray group-hover:text-forest-green transition-colors duration-200" />
                        <span>My Profile</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          set_is_profile_open(false);
                          on_settings_click?.();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal hover:bg-off-white transition-all duration-200 group"
                      >
                        <Settings size={16} className="text-medium-gray group-hover:text-forest-green transition-colors duration-200" />
                        <span>Settings</span>
                      </button>
                      
                      <div className="border-t border-light-gray my-1" />
                      
                      <button
                        onClick={() => {
                          set_is_profile_open(false);
                          on_logout?.();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-red-50 transition-all duration-200 group"
                      >
                        <LogOut size={16} className="group-hover:text-error transition-colors duration-200" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};